import { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { RichEditor, isV2PatientVersion } from '@odo/shared-ui';
import { usePatientCard } from '../../hooks/usePatientCard';
import { useProtocolsByPractice } from '../../hooks/useOrthodontistProtocols';
import type { OrthodontistProtocol } from '../../services/orthodontistPreferencesProtocols';

const NOOP_CHANGE = (): void => {};

interface DisclosureButtonProps {
    open: boolean;
    onToggle: () => void;
    label: string;
    className: string;
}

function DisclosureButton({ open, onToggle, label, className }: DisclosureButtonProps) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className={`w-full flex items-center gap-1.5 text-left hover:text-gray-900 transition-colors ${className}`}
        >
            <ChevronRightIcon
                className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-150 ${
                    open ? 'rotate-90' : ''
                }`}
            />
            <span className="truncate">{label}</span>
        </button>
    );
}

interface ProtocolItemProps {
    protocol: OrthodontistProtocol;
}

function ProtocolItem({ protocol }: ProtocolItemProps) {
    const [open, setOpen] = useState(false);

    return (
        <li className="py-2">
            <DisclosureButton
                open={open}
                onToggle={() => setOpen((v) => !v)}
                label={protocol.title}
                className="text-sm font-medium text-gray-800"
            />
            {open && (
                <div className="pl-5 pt-1.5">
                    {protocol.bodyDoc ? (
                        <div className="note-rich-body font-inter">
                            <RichEditor
                                value={protocol.bodyDoc}
                                onChange={NOOP_CHANGE}
                                allowImages={false}
                                readOnly
                                chrome="bare"
                            />
                        </div>
                    ) : (
                        <p className="text-[12px] text-gray-700 font-inter leading-relaxed whitespace-pre-wrap">
                            {protocol.body}
                        </p>
                    )}
                </div>
            )}
        </li>
    );
}

const LOAD_ERROR_MESSAGE = "Couldn't load the orthodontist's protocols";

interface ProtocolListProps {
    protocols: OrthodontistProtocol[];
    className: string;
}

function ProtocolList({ protocols, className }: ProtocolListProps) {
    return (
        <ul className={`overflow-y-auto px-3 divide-y ${className}`}>
            {protocols.map((protocol) => (
                <ProtocolItem key={protocol.id} protocol={protocol} />
            ))}
        </ul>
    );
}

interface ProtocolsViewProps {
    protocols: OrthodontistProtocol[];
    isError: boolean;
}

function ProtocolsSection({ protocols, isError }: ProtocolsViewProps) {
    return (
        <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
                Orthodontist protocols
            </label>
            {isError ? (
                <p className="text-xs text-gray-400">{LOAD_ERROR_MESSAGE}</p>
            ) : (
                <ProtocolList
                    protocols={protocols}
                    className="max-h-80 border border-gray-200 rounded-md bg-gray-50 divide-gray-200"
                />
            )}
        </div>
    );
}

interface OrthodontistProtocolsPanelProps {
    patientId: string;
}

export function OrthodontistProtocolsPanel({ patientId }: OrthodontistProtocolsPanelProps) {
    const { data: patient } = usePatientCard(patientId);
    const isV2 = isV2PatientVersion(patient?.status?.version);
    const { protocols, isError } = useProtocolsByPractice(isV2 ? patient?.practice?.id : undefined);

    if (!isV2 || (!isError && protocols.length === 0)) return null;

    return <ProtocolsSection protocols={protocols} isError={isError} />;
}
